import { useRef, useState } from 'react';
import { motion, useMotionValueEvent, useScroll, useSpring, useTransform } from 'framer-motion';
import { FeatureBadge } from './FeatureBadge';

// Sticky "film strip": scrolling advances through every piece in a set —
// image cross-fades on the right, spec panel swaps on the left, with a
// clickable frame rail. Desktop only (mobile uses the static grid).

const EASE = [0.22, 1, 0.36, 1];

const Frame = ({ item, i, n, progress }) => {
  const seg = 1 / n;
  const start = i * seg;
  const end = start + seg;
  const opacity = useTransform(
    progress,
    [start - seg * 0.14, start + seg * 0.08, end - seg * 0.08, end + seg * 0.14],
    [0, 1, 1, 0]
  );
  const y = useTransform(progress, [start - seg, end + seg], ['7%', '-7%']);
  const scale = useTransform(progress, [start - seg, end + seg], [1.1, 1]);

  return (
    <motion.div
      className="absolute inset-0"
      style={{ opacity }}
      data-testid={`film-frame-${item.code.replace(/\s/g, '-')}`}
    >
      {item.image ? (
        <motion.img
          src={item.image}
          alt={item.name}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ y, scale }}
          onError={(e) => {
            e.currentTarget.style.opacity = 0;
          }}
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-mb-surface">
          <div className="text-center">
            <div className="eyebrow-sm text-gold">Awaiting photography</div>
            <div className="mt-4 font-display italic text-[20px] text-mb-muted">
              {item.name}
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export const SetFilmStrip = ({ set, items = [] }) => {
  const ref = useRef(null);
  const n = items.length;
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end end'],
  });
  const progress = useSpring(scrollYProgress, { stiffness: 130, damping: 30, mass: 0.4 });
  const [active, setActive] = useState(0);

  useMotionValueEvent(progress, 'change', (v) => {
    const next = Math.min(n - 1, Math.max(0, Math.floor(v * n)));
    setActive((prev) => (prev === next ? prev : next));
  });

  const jumpTo = (i) => {
    const el = ref.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY;
    const scrollable = el.offsetHeight - window.innerHeight;
    window.scrollTo({ top: top + ((i + 0.5) / n) * scrollable, behavior: 'smooth' });
  };

  if (!n) return null;
  const item = items[active];

  return (
    <section
      ref={ref}
      className="hidden md:block relative hairline-t border-subtle"
      style={{ height: `${(n + 0.5) * 100}vh` }}
      data-testid="set-film-strip"
    >
      <div className="sticky top-[130px] h-[calc(100vh-130px)] overflow-hidden">
        <div className="h-full max-w-[1400px] mx-auto px-12 grid grid-cols-12 gap-10 items-center">
          {/* LEFT — spec panel */}
          <div className="col-span-5 xl:col-span-4">
            <div className="eyebrow text-gold">
              {set.name} · Piece {String(active + 1).padStart(2, '0')} of{' '}
              {String(n).padStart(2, '0')}
            </div>

            <div className="mt-8 relative min-h-[420px]">
              <motion.div
                key={item.code}
                initial={{ opacity: 0, y: 22 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, ease: EASE }}
                data-testid={`film-panel-${item.code.replace(/\s/g, '-')}`}
              >
                <div className="font-display text-[70px] xl:text-[92px] leading-none text-mb-muted opacity-30 tabular-nums">
                  {String(active + 1).padStart(2, '0')}
                </div>
                <h3 className="mt-4 font-display text-4xl xl:text-5xl text-mb leading-[1.02]">
                  {item.name}
                </h3>
                <div className="mt-4 eyebrow-sm text-gold">
                  {item.pending ? 'Pending' : (item.category || '—')}
                </div>
                <p className="mt-7 text-[14px] text-mb-muted leading-relaxed max-w-md">
                  {item.description}
                </p>

                <div className="mt-9 hairline-t hairline-b border-subtle py-5 grid grid-cols-2 gap-6">
                  <div>
                    <div className="eyebrow-sm text-mb-muted">Dimensions</div>
                    <div className="mt-2 font-display text-[15px] text-mb">
                      {item.dimensions || '—'}
                    </div>
                  </div>
                  <div>
                    <div className="eyebrow-sm text-mb-muted">Leather</div>
                    <div className="mt-2 font-display text-[15px] text-mb">
                      {item.leather || '—'}
                    </div>
                  </div>
                </div>

                {item.features?.length > 0 && (
                  <div className="mt-6 flex flex-wrap gap-2">
                    {item.features.slice(0, 4).map((f, idx) => (
                      <FeatureBadge key={`${item.code}-ff-${idx}`}>{f}</FeatureBadge>
                    ))}
                  </div>
                )}
              </motion.div>
            </div>
          </div>

          {/* RIGHT — film window */}
          <div className="col-span-7 xl:col-span-8 relative">
            <div className="relative aspect-[16/11] bg-mb-surface hairline border-subtle overflow-hidden">
              {items.map((it, i) => (
                <Frame key={it.code} item={it} i={i} n={n} progress={progress} />
              ))}
              <div className="absolute left-5 bottom-5 eyebrow-sm text-gold bg-mb/80 px-3 py-2">
                {item.category || 'Leather goods'}
              </div>
            </div>

            {/* frame rail */}
            <div className="mt-6 flex items-center gap-3">
              {items.map((it, i) => (
                <button
                  key={`rail-${it.code}`}
                  type="button"
                  onClick={() => jumpTo(i)}
                  data-testid={`film-rail-${it.code.replace(/\s/g, '-')}`}
                  className={`relative flex-1 h-[52px] overflow-hidden hairline transition-colors ${
                    i === active ? 'border-gold' : 'border-subtle hover:border-mb-muted'
                  }`}
                  aria-label={`Go to ${it.name}`}
                >
                  {it.image ? (
                    <img
                      src={it.image}
                      alt=""
                      className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300"
                      style={{ opacity: i === active ? 1 : 0.35 }}
                      onError={(e) => {
                        e.currentTarget.style.opacity = 0;
                      }}
                    />
                  ) : (
                    <span className="absolute inset-0 flex items-center justify-center bg-mb-surface font-display text-[9px] tracking-[0.24em] uppercase text-mb-muted px-1 text-center">
                      {it.name}
                    </span>
                  )}
                </button>
              ))}
            </div>
            <div className="mt-4 flex items-center justify-between">
              <div className="eyebrow-sm text-mb-muted">Scroll to advance the strip</div>
              <div className="h-[0.5px] flex-1 mx-6 bg-[var(--mb-border)] relative">
                <motion.div
                  className="absolute inset-y-0 left-0 bg-gold origin-left"
                  style={{ scaleX: progress, width: '100%' }}
                />
              </div>
              <div className="eyebrow-sm text-gold tabular-nums">
                {String(active + 1).padStart(2, '0')}/{String(n).padStart(2, '0')}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SetFilmStrip;
